import React, { useCallback, useEffect, useState } from "react";

import {
  type Chart,
  CategoryScale,
  Chart as ChartJS,
  type ChartData,
  type ChartDataset,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  type ScatterDataPoint,
  type TimeUnit,
  Title,
  Tooltip,
  ArcElement,
} from "chart.js";
import { formatInTimeZone } from "date-fns-tz";
import { omit } from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { useOrderSummaryGraphLoading } from "@/atoms/order-page-atoms";
import { ANNOTATIONS_DATE_FORMAT } from "@/constants/date";
import { LoadingSlideNoText } from "@components/title-slide-view";
import { useUser } from "@components/user-context";
import {
  useBuildAnnotationDataset,
  annotationPlugin,
} from "@features/custom-annotations";
import { type AnnotationDataset } from "@features/custom-annotations";
import { debouncedResize } from "@features/sales-v3/utils/chart";
import { useNorthbeamQuery } from "@hooks/http";
import {
  type FetchOrderSummaryGraph,
  type FetchOrderSummaryGraphVariables,
  type FetchOrderSummaryGraph_me_orderSummaryGraph_data,
} from "@north-beam/nb-client-side";
import { MetricFormatters } from "@north-beam/nb-client-side";
import { Currencies } from "@north-beam/nb-common";
import {
  APIGranularity,
  useOrderGraphFilters,
} from "@pages/orders/use-orders-filters";
import { FETCH_ORDER_SUMMARY_GRAPH } from "@pages/settings/queries";
import { Badge } from "@shared/badge";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

function granularityToUnit(granularity: APIGranularity): TimeUnit {
  switch (granularity) {
    case APIGranularity.DAY:
      return "day";
    case APIGranularity.HOUR:
      return "hour";
    case APIGranularity.MONTH:
      return "month";
    case APIGranularity.WEEK:
      return "week";
  }
}

const buildDataset = (
  graphData?: FetchOrderSummaryGraph_me_orderSummaryGraph_data[],
  annotationDataset?: ReturnType<typeof useBuildAnnotationDataset>,
): ChartData<"line", number[] | AnnotationDataset["data"]> => {
  if (!graphData) {
    return { datasets: [] };
  }

  const ordersDataSet: ChartDataset<"line", number[]>["data"] = [];
  const revenueDataSet: ChartDataset<"line", number[]>["data"] = [];

  const dateLabels: ChartData<"line", ScatterDataPoint[], string>["labels"] =
    [];

  graphData.forEach(({ datetime, orderCount, orderRevenue }) => {
    ordersDataSet.push(orderCount);
    revenueDataSet.push(orderRevenue);
    dateLabels.push(datetime);
  });

  const datasets: (
    | ChartDataset<"line", number[]>
    | ReturnType<typeof useBuildAnnotationDataset>
  )[] = [
    {
      label: "Orders",
      data: ordersDataSet,
      borderColor: "#B6B4F7",
      backgroundColor: "rgba(183, 181, 247, 0.36)",
      yAxisID: "ordersY",
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - it is allowed, but API doesn't show it
      type: "bar",
      maxBarThickness: 30,
    },
    {
      label: "Revenue",
      pointRadius: 0,
      data: revenueDataSet,
      borderColor: "#514FB2",
      backgroundColor: "#514FB2",
      yAxisID: "revenueY",
    },
  ];

  if (annotationDataset) {
    datasets.push(annotationDataset);
  }

  return {
    labels: dateLabels,
    datasets,
  };
};

export function OrderSummaryGraph() {
  const { user } = useUser();
  const { dateRange, granularity, ...restOfFilters } = useOrderGraphFilters();

  const annotationDataset = useBuildAnnotationDataset(
    formatInTimeZone(dateRange.start, user.timezone, ANNOTATIONS_DATE_FORMAT),
    formatInTimeZone(dateRange.end, user.timezone, ANNOTATIONS_DATE_FORMAT),
    user.timezone,
  );

  const { data: graphResponse, loading } = useNorthbeamQuery<
    FetchOrderSummaryGraph,
    FetchOrderSummaryGraphVariables
  >(FETCH_ORDER_SUMMARY_GRAPH, {
    variables: {
      filterOptions: omit(restOfFilters, "compareDateRange"),
      dateRange,
      granularity,
    },
  });
  const [, setOrderQueryLoading] = useOrderSummaryGraphLoading();

  const { datasets, labels } = buildDataset(
    graphResponse?.me.orderSummaryGraph.data,
    annotationDataset,
  );

  const [chartRef, setChartRef] = useState<ChartJSOrUndefined<any>>();

  useEffect(() => {
    setOrderQueryLoading(loading);
  }, [loading, setOrderQueryLoading]);

  useEffect(() => {
    chartRef && debouncedResize(chartRef);
  }, [chartRef, dateRange, restOfFilters]);

  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined<any> | null) => {
      if (instance) {
        setChartRef(instance);
      }
    },
    [],
  );

  return (
    <div className="relative m-auto w-[99%] h-[20rem]">
      {loading && (
        <div className="absolute w-36 left-[50%] top-[35%]">
          <LoadingSlideNoText className="w-auto" />
        </div>
      )}
      <div className="flex -mb-6 text-sm w-full">
        <Badge text="Orders" badgeColor="#B6B4F7" />
        <Badge
          text="Revenue"
          badgeColor="#514FB2"
          className="ml-auto"
          reversedOrder
        />
      </div>
      <Line
        ref={setChartInstance}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          onResize(chart: Chart) {
            // on resize, reload the annotations
            debouncedResize(chart);
          },
          plugins: {
            title: {
              display: true,
              align: "start",
              font: {
                size: 18,
              },
            },
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: function (tooltipItem) {
                  if (tooltipItem.dataset.label === "Revenue") {
                    return `${tooltipItem.dataset.label}: ${
                      Currencies.getModel(user.currencies.currentCurrency)
                        .symbol
                    }${tooltipItem.formattedValue}`;
                  }
                  return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
                },
                title: function (tooltipItems) {
                  const datetime = moment.tz(
                    tooltipItems[0].parsed.x,
                    user.timezone,
                  );

                  if (granularity === APIGranularity.DAY) {
                    return datetime.format("dddd, MMMM Do YYYY");
                  } else if (granularity === APIGranularity.MONTH) {
                    return datetime.format("MMMM YYYY");
                  } else if (granularity === APIGranularity.WEEK) {
                    let potentialEndDate = datetime.clone().add(6, "days");
                    const dateRangeEnd = moment(dateRange.end);
                    if (potentialEndDate.isSameOrAfter(dateRangeEnd, "day")) {
                      potentialEndDate = dateRangeEnd;
                    }

                    return `${datetime.format(
                      "ddd, MMM Do YYYY",
                    )} - ${potentialEndDate.format("ddd, MMM Do YYYY")}`;
                  }
                  return datetime.format("dddd, MMMM Do YYYY, h:mm A z");
                },
              },
            },
          },
          scales: {
            xAxis: {
              type: "time",
              time: {
                unit: granularityToUnit(granularity),
              },
              ticks: {
                padding: 10,
                maxRotation: 0,
                autoSkip: true,
                font: {
                  family: "Commons Pro",
                  size: 13,
                },
                ...(granularity === APIGranularity.HOUR && {
                  major: {
                    enabled: true,
                  },
                }),
              },
              grid: {
                drawOnChartArea: false,
                drawTicks: true,
              },
            },
            revenueY: {
              ticks: {
                callback(tickValue) {
                  return MetricFormatters.currency(
                    Number(tickValue),
                    user.currencies.currentCurrency,
                    {
                      precision: "approximate",
                    },
                  );
                },
                font: {
                  family: "Commons Pro Mono",
                  size: 11,
                },
              },
              beginAtZero: true,
              type: "linear",
              display: true,
              position: "right",
              grid: {
                drawOnChartArea: false,
              },
            },
            ordersY: {
              type: "linear",
              display: true,
              position: "left",
              ticks: {
                font: {
                  family: "Commons Pro Mono",
                  size: 11,
                },
              },
            },
          },
          layout: {
            padding: {
              bottom: 36,
            },
          },
        }}
        plugins={[annotationPlugin]}
        data={{
          labels,
          datasets,
        }}
      />
    </div>
  );
}
